import React from "react";
import { useGetBillingUsageQuery, useGetHomeQuery } from "@/store/rest_api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";

interface UsageMeterProps {
  label: string;
  used: number;
  included?: number;
  unit: string;
}

const UsageMeter: React.FC<UsageMeterProps> = ({ label, used, included, unit }) => {
  const usedDisplay = Number.isInteger(used) ? used.toString() : used.toFixed(1);

  if (included === undefined || included === null) {
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span>{label}</span>
          <span className="text-muted-foreground">
            {usedDisplay} {unit} · unlimited
          </span>
        </div>
      </div>
    );
  }

  const percent = included > 0 ? Math.min(100, (used / included) * 100) : used > 0 ? 100 : 0;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="text-muted-foreground">
          {usedDisplay} of {included} {unit}
        </span>
      </div>
      <Progress value={percent} />
    </div>
  );
};

export function UsageOverview() {
  const { data: usage, isLoading } = useGetBillingUsageQuery();
  const { data: home } = useGetHomeQuery();

  const onTrial = home?.activeOrganisation?.subscriptionStatus === "TRIAL";

  const formatMoney = (value: number) => `$${value.toFixed(2)}`;

  if (isLoading || !usage) {
    return (
      <div className="container p-4 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid lg:grid-cols-2 gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  return (
    <div className="container p-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Usage</h1>
        <p className="text-muted-foreground">
          {onTrial ? (
            <>What your trial includes and how much you have used so far.</>
          ) : (
            <>
              Billing cycle {new Date(usage.periodStart).toLocaleDateString()} to{" "}
              {new Date(usage.periodEnd).toLocaleDateString()}
            </>
          )}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Calls</CardTitle>
            <CardDescription>{onTrial ? "Trial minutes included and used" : "Minutes used this cycle"}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <UsageMeter
              label="Outbound"
              used={usage.outboundCallMinutesUsed}
              included={usage.outboundCallMinutesIncluded}
              unit="min"
            />
            <UsageMeter
              label="Inbound"
              used={usage.inboundCallMinutesUsed}
              included={usage.inboundCallMinutesIncluded}
              unit="min"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Messages</CardTitle>
            <CardDescription>{onTrial ? "Trial SMS included and used" : "SMS segments used this cycle"}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <UsageMeter
              label="Outbound"
              used={usage.outboundSmsUsed}
              included={usage.outboundSmsIncluded}
              unit="SMS"
            />
            <UsageMeter
              label="Inbound"
              used={usage.inboundSmsUsed}
              included={usage.inboundSmsIncluded}
              unit="SMS"
            />
          </CardContent>
        </Card>

        {!onTrial && (
          <Card>
            <CardHeader>
              <CardTitle>AI Agents</CardTitle>
              <CardDescription>Talk time used this cycle</CardDescription>
            </CardHeader>
            <CardContent>
              <UsageMeter
                label="AI minutes"
                used={usage.aiAgentMinutesUsed}
                included={usage.aiAgentMinutesIncluded}
                unit="min"
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Property Data</CardTitle>
            <CardDescription>Imported leads this cycle</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <UsageMeter
              label="Leads"
              used={usage.propertyData.usedLeads}
              included={usage.propertyData.includedLeads}
              unit="leads"
            />
            <p className="text-xs text-muted-foreground">
              {usage.propertyData.remainingLeads} remaining, then $
              {(usage.propertyData.overagePerLeadCents / 100).toFixed(2)} per lead.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Skip Tracing</CardTitle>
            <CardDescription>Pay per hit, billed from your balance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Hits this cycle</p>
                <p className="text-lg font-medium">{usage.skipTraceHits}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Spend this cycle</p>
                <p className="text-lg font-medium">{formatMoney(usage.skipTraceSpend)}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Price per hit from</p>
                <p className="text-lg font-medium">{formatMoney(usage.skipTraceRatePerHit)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Voicemail Detection</CardTitle>
            <CardDescription>Pay per answered call, billed from your balance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Calls this cycle</p>
                <p className="text-lg font-medium">{usage.machineDetectionCount}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Spend this cycle</p>
                <p className="text-lg font-medium">{formatMoney(usage.machineDetectionSpend)}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Price per call</p>
                <p className="text-lg font-medium">{formatMoney(usage.machineDetectionRatePerCall)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
